import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { environment } from '../environments/environment';
import { UserCredentialsService } from './user-credentials.service';
import { UserAccountService } from './user-account.service';
import { CookieService } from 'ngx-cookie-service';
import { Constants } from './util/constants';

export interface ImpersonationInfo {
  isImpersonated: boolean;
  agentEmail: string;
  impersonatedUser: string;
  expiryTime: number;
  permissions: string;
  impToken: string;
  requestId: string;
  // Agent session state
  agentSessionState?: 'READ_ONLY' | 'PENDING' | 'AUTHORIZED' | 'EXPIRED';
  upgradeRequestId?: string;
  intent?: string;
  pendingStartTime?: number; // Timestamp when PENDING state started
  lastEmailSentTime?: number; // Timestamp when last email was sent (for resend timer)
  requestAttempts?: number; // Number of permission requests made
}

export type TokenSource = 'URL' | 'UPGRADE' | 'REFRESH' | 'RESTORE';

@Injectable({
  providedIn: 'root'
})
export class ImpersonationService {
  private impersonationSubject = new BehaviorSubject<ImpersonationInfo | null>(null);
  public impersonation$ = this.impersonationSubject.asObservable();
  private countdownInterval: any;
  private originalUserToken: string | null = null;
  private lastValidationTime = 0;
  private validationCooldown = 30000; // 30 seconds between validations
  private urlTokenProcessing = false;

  constructor(
    private userCredentials: UserCredentialsService,
    private userAccountService: UserAccountService,
    private http: HttpClient,
    private cookieService: CookieService
  ) {
    // Try to restore impersonation state from localStorage on service initialization
    this.restoreImpersonationFromStorage();

    // Listen for logout events to clear impersonation state
    this.setupLogoutListener();
  }

  // =============================================================================
  // CENTRALIZED TOKEN MANAGEMENT SYSTEM
  // =============================================================================

  /**
   * Sets the working token and updates all related services and storage
   * This is the single source of truth for token updates
   *
   * @param token The new impersonation token
   * @param source Where the token came from (URL, UPGRADE, REFRESH, RESTORE)
   * @param storedState Optional stored state to preserve during restoration
   */
  setWorkingToken(token: string, source: TokenSource, storedState?: ImpersonationInfo): boolean {
    try {
      console.log(`Setting working token from source: ${source}`);

      const decoded = this.decodeJWTToken(token);
      if (!decoded || decoded.type !== 'AGENT_IMPERSONATION') {
        console.error('Invalid token provided to setWorkingToken');
        return false;
      }

      // Handle both expires (milliseconds) and exp (seconds) formats
      let expiryTime = decoded.expires;
      if (!expiryTime && decoded.exp) {
        expiryTime = decoded.exp * 1000;
      }

      const currentInfo = this.getCurrentImpersonation();
      const stateSource = source === 'RESTORE' && storedState ? storedState : currentInfo;

      const updatedInfo: ImpersonationInfo = {
        isImpersonated: true,
        agentEmail: decoded.agentId,
        impersonatedUser: decoded.userId,
        expiryTime: expiryTime,
        permissions: decoded.permissions,
        impToken: token,
        requestId: decoded.requestId,
        // Preserve state when refreshing/upgrading, reset for new URL tokens
        agentSessionState: source === 'URL' ? this.determineInitialState(decoded.permissions) : stateSource?.agentSessionState,
        upgradeRequestId: source === 'URL' ? undefined : stateSource?.upgradeRequestId,
        intent: source === 'URL' ? undefined : stateSource?.intent,
        pendingStartTime: source === 'URL' ? undefined : stateSource?.pendingStartTime,
        lastEmailSentTime: source === 'URL' ? undefined : stateSource?.lastEmailSentTime,
        requestAttempts: source === 'URL' ? 0 : (stateSource?.requestAttempts || 0)
      };

      // Update service state
      this.impersonationSubject.next(updatedInfo);

      // Update token services
      this.userCredentials.setSToken(token);
      this.userAccountService.setSToken(token);

      // Save original user token on first impersonation
      if (source === 'URL' && !this.originalUserToken) {
        this.originalUserToken = this.userCredentials.getSToken() || this.userAccountService.getSToken();
      }

      // Start expiry countdown
      this.startExpiryCountdown(expiryTime);

      // Save to storage
      this.saveToStorage();

      console.log(`Working token updated successfully:`, {
        source,
        permissions: decoded.permissions,
        requestId: decoded.requestId,
        expiryTime: new Date(expiryTime).toISOString()
      });

      return true;
    } catch (error) {
      console.error('Error setting working token:', error);
      return false;
    }
  }

  /**
   * Gets the current working token
   */
  getWorkingToken(): string | null {
    const info = this.getCurrentImpersonation();
    return info ? info.impToken : null;
  }

  /**
   * Gets the current working request ID (the ID of the token currently in use)
   */
  getWorkingRequestId(): string | null {
    const info = this.getCurrentImpersonation();
    return info ? info.requestId : null;
  }

  /**
   * Gets the current working permissions
   */
  getWorkingPermissions(): string {
    const info = this.getCurrentImpersonation();
    return info ? info.permissions : 'NONE';
  }

  /**
   * Sets upgrade request in progress (PENDING state)
   * Keeps current working token but tracks upgrade request
   */
  setUpgradeInProgress(upgradeRequestId: string, intent: string): void {
    const currentInfo = this.getCurrentImpersonation();
    if (currentInfo) {
      const updatedInfo: ImpersonationInfo = {
        ...currentInfo,
        agentSessionState: 'PENDING',
        upgradeRequestId: upgradeRequestId,
        intent: intent,
        pendingStartTime: Date.now()
      };

      this.impersonationSubject.next(updatedInfo);
      this.saveToStorage();

      console.log(`Upgrade request in progress:`, {
        workingToken: currentInfo.requestId,
        upgradeRequest: upgradeRequestId,
        intent
      });
    }
  }

  /**
   * Clears upgrade state (back to working with current token normally)
   */
  clearUpgradeState(): void {
    const currentInfo = this.getCurrentImpersonation();
    if (currentInfo) {
      const updatedInfo: ImpersonationInfo = {
        ...currentInfo,
        agentSessionState: this.determineInitialState(currentInfo.permissions),
        upgradeRequestId: undefined,
        intent: undefined,
        pendingStartTime: undefined,
        lastEmailSentTime: undefined,
        requestAttempts: 0
      };

      this.impersonationSubject.next(updatedInfo);
      this.saveToStorage();

      console.log('Cleared upgrade state, back to normal token usage');
    }
  }

  /**
   * Checks if an upgrade is currently in progress
   */
  isUpgradeInProgress(): boolean {
    const info = this.getCurrentImpersonation();
    return info?.agentSessionState === 'PENDING' && !!info.upgradeRequestId;
  }

  /**
   * Saves current state to localStorage
   */
  private saveToStorage(): void {
    const currentInfo = this.getCurrentImpersonation();
    if (currentInfo) {
      this.saveImpersonationToStorage(currentInfo);
    }
  }

  /**
   * Clears all storage and impersonation state
   */
  clearStorage(): void {
    console.log('Clearing impersonation storage and state');
    this.endImpersonation();
  }

  /**
   * Determines initial state based on permissions
   */
  private determineInitialState(permissions: string): 'READ_ONLY' | 'AUTHORIZED' {
    return permissions && permissions.toLowerCase().includes('write') ? 'AUTHORIZED' : 'READ_ONLY';
  }

  // =============================================================================
  // END CENTRALIZED TOKEN MANAGEMENT SYSTEM
  // =============================================================================

  /**
   * Setup listener for logout events to clear impersonation state
   */
  private setupLogoutListener() {
    // Listen for custom logout event
    window.addEventListener('user-logged-out', () => {
      console.log('Received logout event, clearing impersonation state');
      this.endImpersonation();
    });

    // Check periodically if localStorage was cleared externally
    setInterval(() => {
      const currentInfo = this.getCurrentImpersonation();
      if (currentInfo) {
        const stored = localStorage.getItem('impersonation_info');
        if (!stored) {
          console.log('Impersonation localStorage was cleared externally, clearing service state');
          this.impersonationSubject.next(null);
          if (this.countdownInterval) {
            clearInterval(this.countdownInterval);
            this.countdownInterval = null;
          }
        }
        // No longer doing periodic token validation - validation only happens during restoration
      }
    }, 30000); // Check every 30 seconds
  }

  /**
   * Detects impersonation mode from URL impToken parameter
   */
  detectImpersonationFromUrl(impToken: string): boolean {
    if (!impToken || impToken.trim().length === 0) {
      console.log('No impersonation token in URL');
      return false;
    }

    console.log('Processing impersonation token from URL - URL token is SOT');

    // Set flag to prevent localStorage validation from interfering
    this.urlTokenProcessing = true;

    // Check if we're already processing the same token to prevent duplicate processing
    const currentInfo = this.getCurrentImpersonation();
    if (currentInfo && currentInfo.impToken === impToken) {
      setTimeout(() => {
        this.urlTokenProcessing = false;
      }, 5000);
      return true;
    }

    // URL token is Source of Truth - clear any different localStorage state first
    if (currentInfo && currentInfo.impToken !== impToken) {
      this.clearImpersonationFromStorage();
      this.impersonationSubject.next(null);
    }

    // Process URL token and save to localStorage
    const success = this.setWorkingToken(impToken, 'URL');

    if (success) {
      console.log('Successfully processed URL impersonation token');
    } else {
      console.error('Failed to process URL impersonation token');
    }

    // Keep flag active for a few seconds to prevent async localStorage validation from interfering
    setTimeout(() => {
      this.urlTokenProcessing = false;
    }, 5000); // 5 second protection window

    return success;
  }

  /**
   * Decode JWT token (Base64 decode + JSON parse)
   */
  private decodeJWTToken(token: string): any {
    try {
      const decoded = atob(token);
      const tokenObj = JSON.parse(decoded);

      // New token format from refreshImpersonationToken has payload wrapper
      if (tokenObj.payload && typeof tokenObj.payload === 'object') {
        return tokenObj.payload;
      }

      // If no payload wrapper, token might be malformed
      console.error('Token missing expected payload structure:', tokenObj);
      return null;
    } catch (error) {
      console.error('Error decoding JWT token:', error);
      return null;
    }
  }

  /**
   * Get current impersonation info
   */
  getCurrentImpersonation(): ImpersonationInfo | null {
    return this.impersonationSubject.value;
  }

  /**
   * Check if currently impersonated
   */
  isCurrentlyImpersonated(): boolean {
    const info = this.getCurrentImpersonation();
    return info !== null && info.isImpersonated;
  }

  /**
   * Get impToken for API calls (legacy method - use getWorkingToken() instead)
   */
  getImpToken(): string | null {
    return this.getWorkingToken();
  }

  /**
   * Get impersonation token (alias for getImpToken)
   */
  getImpersonationToken(): string | null {
    return this.getImpToken();
  }

  /**
   * Update impersonation token after permission upgrade
   */
  updateImpersonationToken(newToken: string): boolean {
    console.log('Updating impersonation token (legacy method - consider using setWorkingToken)');

    // Use centralized token management
    return this.setWorkingToken(newToken, 'UPGRADE');
  }

  /**
   * Refresh token after permission upgrade
   */
  async refreshTokenAfterUpgrade(): Promise<boolean> {
    try {
      const currentInfo = this.getCurrentImpersonation();
      if (!currentInfo) {
        console.error('No current impersonation to refresh');
        return false;
      }

      // Call backend to get new token with updated permissions
      const response = await fetch(environment.impersonationRefreshTokenURL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${currentInfo.impersonatedUser}:${currentInfo.impToken}`
        },
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        if (data.status === 'success' && data.data?.newToken) {
          // Update token in service
          const success = this.updateImpersonationToken(data.data.newToken);
          if (success) {
            console.log('Token refreshed successfully after permission upgrade');
            return true;
          }
        } else {
          console.warn('Token refresh response was not successful:', data);
        }
      } else if (response.status === 403) {
        console.error('Token refresh failed - impersonation session is no longer valid (403 Forbidden)');
        this.endImpersonation();
        this.promptSessionExpired();
        return false;
      } else {
        console.error('Token refresh failed with status:', response.status);
      }

      // Fallback: Prompt for page refresh
      console.warn('Token refresh failed. Page reload may be required.');
      this.promptTokenRefresh();
      return false;

    } catch (error) {
      console.error('Error refreshing token:', error);
      this.promptTokenRefresh();
      return false;
    }
  }

  /**
   * Handle token refresh failure silently (no user prompt needed for OTA)
   */
  private promptTokenRefresh(): void {
    // Silently handle token refresh failure without user prompt
    console.log('Token permissions updated, continuing with existing session');
    // No page reload needed - let the application continue normally
  }

  /**
   * Prompt user when impersonation session has expired
   */
  private promptSessionExpired(): void {
    alert('Your impersonation session has expired. Please start a new impersonation session.');
  }

  /**
   * Check if token needs refresh based on API error
   */
  shouldRefreshToken(error: any): boolean {
    return error?.status === 403 &&
           this.isCurrentlyImpersonated() &&
           (error?.error?.message?.includes('permission') ||
            error?.error?.message?.includes('token'));
  }

  /**
   * Check if current token is expired
   */
  isTokenExpired(): boolean {
    const info = this.getCurrentImpersonation();
    if (!info) return false;

    return Date.now() >= info.expiryTime;
  }

  /**
   * Check if current token is near expiry (within 5 minutes)
   */
  isTokenNearExpiry(): boolean {
    const info = this.getCurrentImpersonation();
    if (!info) return false;

    const fiveMinutesFromNow = Date.now() + (5 * 60 * 1000);
    return fiveMinutesFromNow >= info.expiryTime;
  }

  /**
   * Automatically refresh token if expired or near expiry
   */
  async autoRefreshIfNeeded(): Promise<boolean> {
    if (!this.isCurrentlyImpersonated()) {
      return true; // No impersonation, nothing to refresh
    }

    if (this.isTokenExpired()) {
      console.warn('Impersonation token has expired, attempting refresh...');
      return await this.refreshTokenAfterUpgrade();
    }

    if (this.isTokenNearExpiry()) {
      console.log('Impersonation token is near expiry, proactively refreshing...');
      return await this.refreshTokenAfterUpgrade();
    }

    return true; // Token is still valid
  }

  /**
   * Get time remaining until expiry (in minutes)
   */
  getTimeRemainingMinutes(): number {
    const info = this.getCurrentImpersonation();
    if (!info) return 0;

    const now = Date.now();
    const remaining = Math.max(0, info.expiryTime - now);
    return Math.floor(remaining / (1000 * 60));
  }

  /**
   * Start countdown timer for session expiry
   */
  private startExpiryCountdown(expiryTime: number) {
    if (this.countdownInterval) {
      clearInterval(this.countdownInterval);
    }

    this.countdownInterval = setInterval(() => {
      const now = Date.now();
      const remaining = Math.max(0, expiryTime - now);

      if (remaining <= 0) {
        console.log('Impersonation token has expired, clearing state');
        this.endImpersonation();
      }
    }, 5000); // Check every 5 seconds for more responsive expiration detection
  }

  /**
   * End impersonation session
   */
  endImpersonation() {
    if (this.countdownInterval) {
      clearInterval(this.countdownInterval);
      this.countdownInterval = null;
    }

    // Restore original user token when impersonation ends
    if (this.originalUserToken) {
      this.userCredentials.setSToken(this.originalUserToken);
      this.userAccountService.setSToken(this.originalUserToken);
      console.log('Restored original user token in both services after impersonation ended');
      this.originalUserToken = null;
    }

    this.impersonationSubject.next(null);

    // Clear localStorage
    this.clearImpersonationFromStorage();
  }


  /**
   * Called when user logs out to ensure impersonation state is cleared
   */
  onUserLogout() {
    console.log('User logged out, clearing impersonation state');
    this.endImpersonation();
  }

  /**
   * Validate impersonation state after user session is loaded
   * Call this when you're sure the user session is initialized
   */
  validateImpersonationAfterLogin() {
    const currentInfo = this.getCurrentImpersonation();

    if (currentInfo && !this.userAccountService.isLoggedIn()) {
      console.log('User not logged in but impersonation state exists, clearing it');
      this.endImpersonation();
    }
  }

  /**
   * Check if impersonation banner should be visible
   * Banner should only show if there's valid impersonation state
   * We don't check isLoggedIn() because impersonation itself indicates an active agent session
   */
  shouldShowImpersonationBanner(): boolean {
    const currentInfo = this.getCurrentImpersonation();
    if (!currentInfo || !currentInfo.isImpersonated) {
      return false;
    }

    // Check if impersonation token is still valid (not expired)
    if (Date.now() >= currentInfo.expiryTime) {
      console.log('Impersonation token has expired, hiding banner');
      this.endImpersonation();
      return false;
    }

    // Check if we have a valid impersonation token
    const impToken = this.getImpToken();
    if (!impToken) {
      console.log('No impersonation token available, hiding banner');
      return false;
    }

    // Token validation is now done during restoration only
    // No need for frequent validation that could clear state prematurely

    return true;
  }

  /**
   * Asynchronously validate token using /verifyAuth endpoint
   */
  private async validateTokenAsync(): Promise<void> {
    try {
      const isValid = await this.verifyTokenWithBackend();
      if (!isValid) {
        console.log('Token validation failed with /verifyAuth endpoint - but allowing natural expiry');
        // Don't immediately clear - let natural expiry timer handle it
        // This prevents premature clearing due to transient issues
      }
    } catch (error) {
      console.error('Token validation error:', error);
      // Don't clear on validation errors - let natural expiry handle cleanup
      console.log('Token validation threw error - allowing natural expiry to handle cleanup');
    }
  }

  /**
   * Verify token validity using /verifyAuth endpoint
   */
  private async verifyTokenWithBackend(): Promise<boolean> {
    const currentInfo = this.getCurrentImpersonation();
    if (!currentInfo) {
      return false;
    }

    try {
      // Use same format as UserAccountService - GET method with Authorization header
      const headers = new HttpHeaders()
        .set('Authorization', `Bearer ${currentInfo.impersonatedUser}:${currentInfo.impToken}`)
        .set(Constants.PARAM_RS_CLIENT_COOKIE, this.cookieService.get('RS-CLIENT'));

      const response = await this.http.get(environment.apiForCredentialsVerfication, { headers }).toPromise();

      // Check if response indicates success - API returns {success: true}
      return response && (response as any).success === true;

    } catch (error: any) {
      console.error('Token verification error:', error);

      // Only treat as definitively invalid for specific error codes
      if (error.status === 401 || error.status === 403) {
        console.log(`Token verification failed with status ${error.status} - token is expired/invalid`);
        return false;
      }

      // For other errors (network issues, etc.), assume token is still valid
      // This prevents clearing state due to temporary connectivity issues
      console.warn(`Token verification failed with status ${error.status} - assuming token is still valid`);
      return true;
    }
  }

  /**
   * Update agent session state in current impersonation
   */
  updateAgentSessionState(state: 'READ_ONLY' | 'PENDING' | 'AUTHORIZED' | 'EXPIRED', upgradeRequestId?: string, intent?: string): void {
    const currentInfo = this.getCurrentImpersonation();
    if (currentInfo) {
      const updatedInfo: ImpersonationInfo = {
        ...currentInfo,
        agentSessionState: state,
        upgradeRequestId: upgradeRequestId || currentInfo.upgradeRequestId,
        intent: intent || currentInfo.intent,
        // Store timestamp when entering PENDING state
        pendingStartTime: state === 'PENDING' ? Date.now() : currentInfo.pendingStartTime
      };

      this.impersonationSubject.next(updatedInfo);
      this.saveImpersonationToStorage(updatedInfo);
      console.log('Updated agent session state to:', state);
    }
  }

  /**
   * Record when an email was sent for resend timer tracking
   */
  recordEmailSent(): void {
    const currentInfo = this.getCurrentImpersonation();
    if (currentInfo) {
      const updatedInfo: ImpersonationInfo = {
        ...currentInfo,
        lastEmailSentTime: Date.now()
      };

      this.impersonationSubject.next(updatedInfo);
      this.saveImpersonationToStorage(updatedInfo);
      console.log('Recorded email sent time for resend timer');
    }
  }

  /**
   * Increment and track request attempts
   */
  incrementRequestAttempts(): number {
    const currentInfo = this.getCurrentImpersonation();
    if (currentInfo) {
      const newAttempts = (currentInfo.requestAttempts || 0) + 1;
      const updatedInfo: ImpersonationInfo = {
        ...currentInfo,
        requestAttempts: newAttempts
      };

      this.impersonationSubject.next(updatedInfo);
      this.saveImpersonationToStorage(updatedInfo);
      console.log('Incremented request attempts to:', newAttempts);
      return newAttempts;
    }
    return 0;
  }

  /**
   * Reset request attempts counter (used on successful authorization)
   */
  resetRequestAttempts(): void {
    const currentInfo = this.getCurrentImpersonation();
    if (currentInfo) {
      const updatedInfo: ImpersonationInfo = {
        ...currentInfo,
        requestAttempts: 0
      };

      this.impersonationSubject.next(updatedInfo);
      this.saveImpersonationToStorage(updatedInfo);
      console.log('Reset request attempts counter');
    }
  }

  /**
   * Complete the impersonation session and revoke permissions on backend
   * This marks the session as COMPLETED and sends audit trail email
   */
  async completeSession(): Promise<boolean> {
    try {
      const currentInfo = this.getCurrentImpersonation();
      if (!currentInfo) {
        console.error('No current impersonation session to complete');
        return false;
      }

      console.log('Completing impersonation session and revoking backend permissions...');

      const headers = new HttpHeaders()
        .set('Authorization', `Bearer ${currentInfo.impersonatedUser}:${currentInfo.impToken}`)
        .set('Content-Type', 'application/json')
        .set(Constants.PARAM_RS_CLIENT_COOKIE, this.cookieService.get('RS-CLIENT'));

      const response = await this.http.post(environment.impersonationCompleteSessionURL, {}, { headers }).toPromise();

      if (response && (response as any).status === 'success') {
        console.log('Session completed successfully on backend');
        // Note: Storage clearing is handled by the caller (footer component) via endImpersonation()
        return true;
      } else {
        console.error('Failed to complete session on backend:', response);
        return false;
      }

    } catch (error: any) {
      console.error('Error completing impersonation session:', error);
      return false;
    }
  }

  /**
   * Save impersonation info to localStorage
   */
  private saveImpersonationToStorage(impInfo: ImpersonationInfo): void {
    try {
      localStorage.setItem('impersonation_info', JSON.stringify(impInfo));
      console.log('Saved impersonation info to localStorage');
    } catch (error) {
      console.error('Failed to save impersonation info to localStorage:', error);
    }
  }

  /**
   * Restore impersonation info from localStorage
   */
  private async restoreImpersonationFromStorage(): Promise<void> {
    try {
      // IMPORTANT: Don't check URL here - Angular router navigation happens after service construction
      // App component will handle URL tokens as SOT, this method only handles localStorage restoration
      const stored = localStorage.getItem('impersonation_info');
      if (stored) {
        const impInfo: ImpersonationInfo = JSON.parse(stored);

        // Check if token is still valid (not expired based on local time)
        if (Date.now() < impInfo.expiryTime) {
          console.log('Stored token not expired locally, validating with backend...');

          // Use centralized token management for restoration with preserved state
          const success = this.setWorkingToken(impInfo.impToken, 'RESTORE', impInfo);

          if (success) {
            // Verify with backend during restoration
            const isValid = await this.verifyTokenWithBackend();

            if (isValid) {
              console.log('Stored impersonation token validated and restored successfully');
            } else {
              // Only clear state if URL token processing isn't in progress
              if (!this.urlTokenProcessing) {
                console.log('Stored impersonation token validation failed, clearing invalid state');
                this.clearStorage();
              }
            }
          } else {
            console.log('Failed to restore stored token, clearing storage');
            this.clearStorage();
          }
        } else {
          console.log('Stored impersonation token has expired, clearing storage');
          this.clearStorage();
        }
      } else {
        console.log('No stored impersonation info found');
      }
    } catch (error) {
      console.error('Failed to restore impersonation from localStorage:', error);
      this.clearStorage();
    }
  }

  /**
   * Clear impersonation info from localStorage using centralized method
   */
  public clearImpersonationFromStorage(): void {
    this.userAccountService.clearImpersonationStorage();
  }
}